import { DefaultPageHeader } from 'layouts';
import type { ChannelDetail, EntityOptions } from 'models';
import { Card, Col, Form, Paragraph, Row, Skeleton } from 'ui';
import { useTextCollapseExpand } from 'common/hooks';
import { type ChannelEntityOption, ChannelsSlider, EntityTypeRadioPills } from '../components';
import { useChannelDetailQuery } from '../queries';
import type { ChannelEntityType } from '../types';
import { AllEntities } from './all-entities';

const getEntityTypes = (channel: ChannelDetail) => {
  const entityOptions: ChannelEntityOption[] = [];

  if (channel.communityCount > 0) {
    entityOptions.push('community');
  }

  if (channel.eventCount > 0) {
    entityOptions.push('event');
  }

  if (channel.programCount > 0) {
    entityOptions.push('program');
  }

  if (channel.givingCount > 0) {
    entityOptions.push('giving');
  }

  if (channel.videoCount > 0) {
    entityOptions.push('video');
  }

  return entityOptions;
};

type ChannelDetailFormData = {
  subject?: ChannelEntityType;
};

type P = {
  channelId: string;
  getPathToEntityDetail: (id: string, options: EntityOptions) => string;
};

const CustomChannelsDetail = ({ channelId, getPathToEntityDetail }: P) => {
  const [form] = Form.useForm<ChannelDetailFormData>();

  const { data: channel, isLoading: isLoadingChannel } = useChannelDetailQuery({ channelId });

  const { collapseExpandButton, ellipsis } = useTextCollapseExpand({
    text: channel?.description,
  });

  return (
    <>
      {isLoadingChannel || !channel ? (
        <Skeleton active paragraph={{ rows: 12 }} />
      ) : (
        <>
          <DefaultPageHeader title={channel.name} />
          {channel.description ? (
            <Row className="custom-channels-detail__description" key={channel.description}>
              <Col span={12}>
                <Paragraph ellipsis={ellipsis}>{channel.description}</Paragraph>
                {collapseExpandButton}
              </Col>
            </Row>
          ) : null}
          <ChannelsSlider
            channelId={channel.id}
            controls={{ title: 'Featured' }}
            getPathToEntityDetail={getPathToEntityDetail}
          />
          <Form<ChannelDetailFormData> form={form} initialValues={{ subject: undefined }}>
            <Form.Item<ChannelDetailFormData> name="subject" noStyle>
              <EntityTypeRadioPills options={getEntityTypes(channel)} />
            </Form.Item>
            <Form.Item<ChannelDetailFormData> dependencies={['subject']} noStyle>
              {({ getFieldsValue }) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                const { subject } = getFieldsValue(true) as Partial<ChannelDetailFormData>;

                return (
                  <Card>
                    <AllEntities
                      channelId={channelId}
                      getPathToEntityDetail={getPathToEntityDetail}
                      subject={subject}
                    />
                  </Card>
                );
              }}
            </Form.Item>
          </Form>
        </>
      )}
    </>
  );
};

export default CustomChannelsDetail;
